import { useCallback, useEffect, useRef, useState } from "react";

/**
 * 通用数据请求 hook：管理 loading / error / data 三态，并提供 reload。
 *
 * @param fetcher 返回 Promise 的请求函数
 * @param deps    依赖数组；其中任一变化会重新请求（默认 []，仅挂载时请求一次）
 *
 * 用法：
 *   useRequest(getDashboardSummary)               // 只请求一次
 *   useRequest(() => getApiTrends(days), [days])  // days 变化时重新请求
 */
export function useRequest<T>(
  fetcher: () => Promise<T>,
  deps: React.DependencyList = []
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 用 ref 持有最新 fetcher，避免把它塞进 effect 依赖
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // reload 通过自增计数触发 effect 重跑
  const [reloadTick, setReloadTick] = useState(0);
  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetcherRef
      .current()
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Request failed.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTick, ...deps]);

  return { data, loading, error, reload };
}
